import {
  Box,
  IconButton,
  Modal,
  Stack,
  Typography,
} from '@ace-clublabs/shared-component-library';
import { useCallback, useEffect, useRef, useState } from 'react';

import { SearchResultsList } from '@/components/shared/SearchBox';
import { useSearchBox } from '@/components/shared/SearchBox/useSearchBox';
import Chip from '@/features/appointments/components/Chip';
import SearchContainer from './SearchContainer';
import SearchIconButton from './SearchIconButton';
import SearchTextField from './SearchTextField';
import type { RSSearchBoxProps } from './types';

const RSSearchBoxMobile: React.FC<RSSearchBoxProps> = ({
  sx,
  placeholder,
  showWarning,
  value,
  selectedResult: controlledSelected,
  onSelect,
  onChange,
  searchResults,
  isLoading = false,
  onContinueClick,
  onOpenChange,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;

  const setPanelOpen = (next: boolean) => {
    setOpen(next);
    onOpenChangeRef.current?.(next);
  };

  const handleModalContinueRef = useRef<() => void>(() => {});

  const {
    displayValue,
    selectedResult,
    handleSearchChange,
    handleSelect,
    handleClearSelected,
    textFieldRef,
    handleKeyDown,
  } = useSearchBox({
    value,
    selectedResult: controlledSelected,
    onSelect,
    onChange,
    searchResults,
    onConfirm: () => handleModalContinueRef.current(),
  });

  const canContinue = !!selectedResult || !!displayValue.trim();

  const handleModalContinue = useCallback(() => {
    if (!canContinue) {
      onContinueClick?.();
      return;
    }
    setOpen(false);
    onOpenChangeRef.current?.(false);
    onContinueClick?.();
  }, [canContinue, onContinueClick]);

  handleModalContinueRef.current = handleModalContinue;

  const handleOpen = () => {
    setPanelOpen(true);
  };

  useEffect(() => {
    return () => onOpenChangeRef.current?.(false);
  }, []);

  return (
    <>
      <SearchContainer sx={sx} data-testid='rs-search-box-mobile-trigger'>
        <Stack
          direction='row'
          spacing={2}
          onClick={handleOpen}
          aria-haspopup='dialog'
          aria-expanded={open ? 'true' : 'false'}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 0.5,
            pl: 1,
          }}
          data-testid='rs-search-box-mobile-trigger-content'
        >
          {selectedResult ? (
            <Chip label={selectedResult.title} onDelete={handleClearSelected} />
          ) : (
            <Typography
              sx={{
                textAlign: 'left',
                color: 'grey.500',
              }}
            >
              {placeholder}
            </Typography>
          )}
          <SearchIconButton data-testid='rs-search-box-mobile-trigger-search-button' />
        </Stack>
      </SearchContainer>

      <Modal
        open={open}
        onClose={() => setPanelOpen(false)}
        variants='bottom-sheet'
        sx={{}}
        slotProps={{
          paper: {
            sx: {
              height: '100%',
              maxHeight: '100dvh',
              pb: 0,
              borderRadius: 0,
            },
          },
        }}
        data-testid='rs-search-box-mobile-modal'
      >
        <Stack
          direction='column'
          spacing={1}
          sx={{
            height: '100%',
          }}
        >
          <Stack
            direction='row'
            spacing={2}
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Typography variant='b1Bold'>Search destinations</Typography>
            <IconButton
              iconName='xmark-regular'
              onClick={() => setPanelOpen(false)}
              data-testid='rs-search-box-mobile-close-button'
            />
          </Stack>
          <SearchContainer
            ref={containerRef}
            sx={sx}
            data-testid='rs-search-box-mobile-search-container'
          >
            <Stack
              direction='row'
              spacing={1}
              aria-haspopup='dialog'
              aria-expanded={open ? 'true' : 'false'}
              sx={(theme) => ({
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 0.5,
                '&:hover': {
                  backgroundColor: 'primary.100',
                  '& .MuiInputBase-root': {
                    backgroundColor: `${theme.palette.primary[100]} !important`,
                  },
                },
              })}
            >
              <SearchTextField
                ref={textFieldRef}
                placeholder={placeholder}
                placeholderColor={showWarning ? '#be3317' : undefined}
                value={displayValue}
                onChange={handleSearchChange}
                selectedResult={selectedResult}
                onClearSelected={handleClearSelected}
                onKeyDown={handleKeyDown}
                autofocus
              />
              <SearchIconButton
                loading={isLoading}
                canContinue={canContinue}
                onClick={handleModalContinue}
                data-testid='rs-search-box-mobile-modal-search-button'
              />
            </Stack>
          </SearchContainer>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <SearchResultsList
              onSelect={handleSelect}
              open={
                (value !== '' && !!searchResults) || !!searchResults?.length
              }
              results={searchResults}
            />
          </Box>
        </Stack>
      </Modal>
    </>
  );
};

export default RSSearchBoxMobile;
